import { cn } from '@/lib/misc/Utils'
import { m } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'

export interface TabElement {
  id: string
  label: string
  labelContent?: React.ReactNode
  content?: React.ReactNode
  disabled?: boolean
  className?: string
}

interface TabList2Props {
  tabs: TabElement[]
  selectedTab: string | null
  onTabClick: (tab: string) => void
  className?: string
}

const TabList2 = ({
  tabs,
  selectedTab,
  onTabClick,
  className,
}: TabList2Props) => {
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([])
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 })

  useEffect(() => {
    const selectedIndex = tabs.findIndex((tab) => tab.id === selectedTab)
    if (selectedIndex !== -1 && tabRefs.current[selectedIndex]) {
      const tabElement = tabRefs.current[selectedIndex]
      if (tabElement) {
        requestAnimationFrame(() => {
          setIndicatorStyle({
            left: tabElement.offsetLeft,
            width: tabElement.offsetWidth,
          })
        })
      }
    }
  }, [selectedTab, tabs])

  return (
    <div
      role="tablist"
      aria-orientation="horizontal"
      className={cn(
        'relative flex w-full gap-x-2 my-4 bg-surface p-1 rounded-xl border-1 border-hairline no-scrollbar',
        className
      )}
      data-orientation="horizontal"
      style={{ outline: 'none' }}
    >
      {/* Animated background indicator */}
      <m.div
        className="absolute top-1 bottom-1 bg-accent-60 rounded-lg"
        animate={{
          left: indicatorStyle.left,
          width: indicatorStyle.width,
        }}
        transition={{
          type: 'spring',
          stiffness: 400,
          damping: 30,
        }}
        style={{
          zIndex: 0,
        }}
      />

      {tabs.map((tab, index) => (
        <button
          type="button"
          role="tab"
          key={tab.id}
          ref={(el) => {
            tabRefs.current[index] = el
          }}
          data-cy={`tab-${tab.id}`}
          data-state={tab.id === selectedTab ? 'active' : 'inactive'}
          className={cn(
            'relative z-10 rounded-lg w-full text-center py-3 px-4 cursor-pointer transition-colors duration-200',
            selectedTab === tab.id
              ? 'text-white hover:opacity-90'
              : 'text-accent-60 hover:text-accent-100',
            tab.disabled && 'opacity-50 cursor-not-allowed'
          )}
          onClick={() => onTabClick(tab.id)}
          data-orientation="horizontal"
          disabled={tab.disabled}
        >
          {tab.labelContent || tab.label}
        </button>
      ))}
    </div>
  )
}

export default TabList2
